import React, { useEffect, useMemo, useState } from "react";
import { getLiveMetaAdsLibrary } from "../services/metaAdsLibraryService";
import "./LiveMetaAdsLibrary.css";
import { addCalendarDays, reportingToday } from "../utils/reportingCalendar";

const DATE_PRESETS = Object.freeze([
  { value: "campaign_flight", label: "Selected campaign flight" },
  { value: "last_7d", label: "Last 7 days" },
  { value: "maximum", label: "Maximum available" },
  { value: "last_30d", label: "Last 30 days" },
  { value: "last_90d", label: "Last 90 days" },
  { value: "year_to_date", label: "Year to date" },
  { value: "custom", label: "Custom dates" },
]);

const SORT_OPTIONS = Object.freeze([
  { value: "spend_desc", label: "Spend: high to low" },
  { value: "impressions_desc", label: "Impressions: high to low" },
  { value: "clicks_desc", label: "Clicks: high to low" },
  { value: "ctr_desc", label: "CTR: high to low" },
  { value: "cpc_desc", label: "CPC: high to low" },
  { value: "cpm_desc", label: "CPM: high to low" },
  { value: "ad_asc", label: "Ad name: A–Z" },
  { value: "campaign_asc", label: "Campaign: A–Z" },
]);

function numeric(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

// Unavailable-but-not-erroneous metrics render as "—". The reason metrics
// are unavailable lives in one reporting-state panel per ad — never repeated
// inside every metric tile. A value of zero is real data and renders as zero.
const METRIC_UNAVAILABLE = "—";

export function formatMetaCurrency(value) {
  const number = numeric(value);
  return number == null
    ? METRIC_UNAVAILABLE
    : number.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
}

export function formatMetaInteger(value) {
  const number = numeric(value);
  return number == null
    ? METRIC_UNAVAILABLE
    : Math.round(number).toLocaleString("en-US");
}

export function formatMetaPercent(value) {
  const number = numeric(value);
  return number == null ? METRIC_UNAVAILABLE : `${number.toFixed(2)}%`;
}

export function formatMetaDecimal(value) {
  const number = numeric(value);
  return number == null ? METRIC_UNAVAILABLE : number.toFixed(2);
}

function formatMetaDate(value) {
  const text = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return "Not returned by Meta";
  const parsed = new Date(`${text}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "Not returned by Meta";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatTimestamp(value) {
  const parsed = new Date(value);
  if (!value || Number.isNaN(parsed.getTime())) return "Not available";
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function humanize(value) {
  const text = String(value || "").trim();
  if (!text || text.toLowerCase() === "unknown") return "Unknown";
  return text
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function reportingDates(performance, fallback) {
  const start = performance?.dateStart || fallback?.dateStart;
  const stop = performance?.dateStop || fallback?.dateStop;
  if (!start && !stop) return fallback?.label || "Maximum available Meta reporting range";
  return `${formatMetaDate(start)} – ${formatMetaDate(stop)}`;
}

function errorPresentation(error) {
  const code = error?.code || "META_TEMPORARY_FAILURE";
  const presentations = {
    ADS_READ_REQUIRED: {
      title: "Meta ads_read authorization required",
      message: "Meta `ads_read` authorization is required to display live ad reporting.",
      showIntegrations: true,
    },
    META_AUTHORIZATION_EXPIRED: {
      title: "Meta authorization expired",
      message: "Renew Meta authorization in Integrations to retrieve live ad reporting.",
      showIntegrations: true,
    },
    META_NOT_CONNECTED: {
      title: "Connect Meta to continue",
      message: "Connect the advertiser’s Meta account in Integrations to display live ad reporting.",
      showIntegrations: true,
    },
    META_USER_OAUTH_REQUIRED: {
      title: "Visible Meta Login required",
      message: "Complete visible Meta Login in Integrations before retrieving live ad reporting.",
      showIntegrations: true,
    },
    META_AD_ACCOUNT_REQUIRED: {
      title: "Linked Meta account required",
      message: "Link the advertiser’s Meta advertising account in Integrations.",
      showIntegrations: true,
    },
    META_RATE_LIMITED: {
      title: "Meta read temporarily limited",
      message: "Meta temporarily limited this read-only request. Try refreshing again shortly.",
    },
    META_ACCOUNT_ISOLATION_FAILED: {
      title: "Linked account could not be verified",
      message: "Cphere could not verify this advertiser’s linked Meta account. No ad data was displayed.",
    },
  };
  return (
    presentations[code] || {
      title: "Live Meta reporting unavailable",
      message: error?.message || "Meta live ad reporting is temporarily unavailable.",
    }
  );
}

const METRIC_LABELS = Object.freeze({
  spend: "Spend",
  impressions: "Impressions",
  reach: "Reach",
  clicks: "Clicks",
  ctr: "CTR",
  cpc: "CPC",
  cpm: "CPM",
  frequency: "Frequency",
});

function restrictionSentence(deliveryRestriction) {
  if (deliveryRestriction === "AD_SET_PAUSED") return "Its parent ad set is paused.";
  if (deliveryRestriction === "CAMPAIGN_PAUSED") return "Its parent campaign is paused.";
  if (deliveryRestriction === "AD_PAUSED") return "The ad itself is paused.";
  return null;
}

// One presentation per structured backend reporting state. The collapsed row
// and the expanded detail panel both derive from this single resolver so they
// can never disagree about the reporting state.
export function adReportingPresentation(ad, platformLabel = "Meta") {
  const reporting = ad?.reporting || null;
  const status = String(reporting?.status || "").toUpperCase();
  const restriction = restrictionSentence(ad?.statusContext?.deliveryRestriction);
  const unavailable = (reporting?.unavailableMetrics || [])
    .map((key) => METRIC_LABELS[key])
    .filter(Boolean);
  const unavailableNote = unavailable.length
    ? `Metrics marked “${METRIC_UNAVAILABLE}” (${unavailable.join(", ")}) were not returned by Meta for this ad-level query.`
    : null;

  if (status === "AVAILABLE") {
    return {
      state: "available",
      showMetrics: true,
      headline: null,
      explanation: unavailableNote,
      retryable: false,
    };
  }
  if (status === "PARTIAL") {
    return {
      state: "partial",
      showMetrics: true,
      headline: "Partial Meta metrics",
      explanation: `Meta returned this ad’s insights without every requested metric. Returned metrics are shown exactly as reported. ${unavailableNote || ""}`.trim(),
      retryable: false,
    };
  }
  if (status === "NO_DELIVERY") {
    // NEVER_DELIVERED means the confirmed-zero evidence covers Meta's
    // maximum reporting range, not just the selected window — say so.
    const base =
      reporting?.explanationCode === "NO_PLATFORM_ATTRIBUTED_DELIVERY"
        ? `Meta returned no ${platformLabel}-attributed ad-level delivery for this ad during the selected reporting period.`
        : reporting?.explanationCode === "NEVER_DELIVERED"
        ? "Meta returned no ad-level delivery for this ad across Meta's maximum reporting range — it has never recorded delivery."
        : "Meta returned no ad-level delivery for this ad during the selected reporting period.";
    return {
      state: "no_delivery",
      showMetrics: false,
      headline: "No delivery recorded",
      explanation: restriction ? `${base} ${restriction}` : base,
      retryable: false,
    };
  }
  if (status === "OUTSIDE_REPORTING_WINDOW") {
    const lifetime = reporting?.diagnostics?.lifetimeDelivery || null;
    const rangeText =
      lifetime?.earliestDelivery && lifetime?.latestDelivery
        ? ` Meta reports delivery for this ad between ${formatMetaDate(lifetime.earliestDelivery)} and ${formatMetaDate(lifetime.latestDelivery)}, outside the selected dates.`
        : " Meta reports delivery for this ad outside the selected dates.";
    return {
      state: "outside_window",
      showMetrics: false,
      headline: "No delivery in selected dates",
      explanation: `${rangeText.trim()} Those out-of-range metrics are not merged into the selected reporting window.`,
      retryable: false,
    };
  }
  if (status === "EMPTY_RESPONSE_UNEXPLAINED") {
    return {
      state: "unexplained_empty",
      showMetrics: false,
      headline: "No insights returned",
      explanation:
        "Meta returned an empty ad-level insights response. Delivery history could not be conclusively determined.",
      retryable: Boolean(reporting?.retryable),
    };
  }
  if (status === "API_ERROR") {
    return {
      state: "retrieval_failed",
      showMetrics: false,
      headline: "Retrieval failed",
      explanation:
        "Meta ad-level reporting could not be retrieved for this ad. This is a retrieval failure — it is not evidence that the ad had no delivery.",
      retryable: Boolean(reporting?.retryable),
    };
  }
  if (status === "PERMISSION_DENIED") {
    return {
      state: "permission_denied",
      showMetrics: false,
      headline: "Reporting permission unavailable",
      explanation:
        "Meta reporting permission is unavailable for this read, so no ad-level metrics could be retrieved. This does not indicate whether the ad delivered.",
      retryable: false,
      showIntegrations: true,
    };
  }
  // Legacy or unknown payloads: fall back honestly without inventing a reason.
  if (ad?.performance?.hasInsightsRow) {
    return { state: "available", showMetrics: true, headline: null, explanation: null, retryable: false };
  }
  return {
    state: "unknown",
    showMetrics: false,
    headline: "Reporting state unavailable",
    explanation:
      "The reporting state for this ad could not be determined from the Meta response.",
    retryable: true,
  };
}

// Honest split of the ads without delivery: confirmed zero (proven by Meta
// evidence) versus ad-level reporting that could not be reliably retrieved.
// Prefers the backend's canonical summary fields; legacy payloads without
// them derive the identical split from each ad's structured reporting state,
// so "unavailable" is never presented as a confirmed zero.
const CONFIRMED_ZERO_REPORTING_STATES = Object.freeze([
  "NO_DELIVERY",
  "OUTSIDE_REPORTING_WINDOW",
]);

export function summaryDeliveryCounts(payload) {
  const summary = payload?.summary || {};
  if (
    summary.adsWithConfirmedZeroDelivery != null ||
    summary.adsWithReportingUnavailable != null
  ) {
    return {
      confirmedZero: summary.adsWithConfirmedZeroDelivery ?? null,
      unavailable: summary.adsWithReportingUnavailable ?? null,
    };
  }
  const ads = Array.isArray(payload?.ads) ? payload.ads : [];
  let confirmedZero = 0;
  let unavailable = 0;
  for (const ad of ads) {
    if (ad?.performance?.hasDelivery) continue;
    const status = String(ad?.reporting?.status || "").toUpperCase();
    if (
      CONFIRMED_ZERO_REPORTING_STATES.includes(status) ||
      (status === "AVAILABLE" && ad?.performance?.hasInsightsRow)
    ) {
      confirmedZero += 1;
    } else {
      unavailable += 1;
    }
  }
  return { confirmedZero, unavailable };
}

// Quantum-only reporting diagnostics for one ad. Rendered solely when the
// host page opts in via showOperatorDiagnostics; the client portal never
// passes that prop. Every field shown here is already masked or sanitized by
// the backend — no tokens, raw Meta IDs, or raw Graph responses exist in the
// payload to expose.
function OperatorDiagnostics({ ad, payload }) {
  const reporting = ad?.reporting || {};
  const diagnostics = reporting.diagnostics || {};
  const lifetime = diagnostics.lifetimeDelivery || null;
  const retrieval = payload?.insightsRetrieval || null;
  const requestedWindow =
    diagnostics.requestedStartDate && diagnostics.requestedEndDate
      ? `${diagnostics.requestedStartDate} → ${diagnostics.requestedEndDate}`
      : diagnostics.requestedDatePreset || "—";
  const lifetimeText = !lifetime
    ? "Not needed — selected window is Meta's maximum range"
    : !lifetime.checked
    ? "Not run"
    : !lifetime.verified
    ? "Checked, unverified — zero cannot be confirmed"
    : lifetime.hasPlatformDelivery
    ? `Verified — delivery ${lifetime.earliestDelivery || "?"} → ${lifetime.latestDelivery || "?"}`
    : "Verified — no lifetime delivery on this platform";
  return (
    <dl
      className="live-meta-operator-diagnostics"
      data-testid="live-meta-operator-diagnostics"
      aria-label={`Reporting diagnostics for ${ad?.adName || "this ad"}`}
    >
      <div>
        <dt>Reporting state</dt>
        <dd className="live-meta-data-value">
          {reporting.status || "UNKNOWN"}
          {reporting.explanationCode ? ` · ${reporting.explanationCode}` : ""}
        </dd>
      </div>
      <div><dt>Reporting level</dt><dd className="live-meta-data-value">{reporting.level || "—"}</dd></div>
      <div><dt>Meta ad ID</dt><dd className="live-meta-data-value">{diagnostics.metaAdIdMasked || "—"}</dd></div>
      <div>
        <dt>Insight rows matched</dt>
        <dd className="live-meta-data-value">{diagnostics.insightRowsMatched ?? "—"}</dd>
      </div>
      <div><dt>Requested window</dt><dd className="live-meta-data-value">{requestedWindow}</dd></div>
      <div><dt>Entity created</dt><dd className="live-meta-data-value">{diagnostics.entityCreatedDate || "—"}</dd></div>
      <div><dt>Lifetime evidence</dt><dd>{lifetimeText}</dd></div>
      <div>
        <dt>Insights retrieval</dt>
        <dd className="live-meta-data-value">
          {retrieval
            ? `${retrieval.status}${retrieval.truncated ? " · truncated" : ""} · ${retrieval.rowsReturned ?? 0} rows`
            : "—"}
        </dd>
      </div>
      {diagnostics.errorCategory ? (
        <div>
          <dt>Error</dt>
          <dd className="live-meta-data-value">
            {diagnostics.errorCategory}
            {diagnostics.sanitizedErrorCode != null
              ? ` (code ${diagnostics.sanitizedErrorCode}${diagnostics.sanitizedErrorSubcode != null ? `/${diagnostics.sanitizedErrorSubcode}` : ""})`
              : ""}
          </dd>
        </div>
      ) : null}
      <div><dt>Correlation</dt><dd className="live-meta-data-value">{diagnostics.requestCorrelationId || "—"}</dd></div>
    </dl>
  );
}

function CreativePreview({ creative, adName }) {
  const [failed, setFailed] = useState(false);
  const url = creative?.previewUrl;
  if (!url || failed) {
    return (
      <div className="live-meta-creative-placeholder" role="img" aria-label={`No Meta preview for ${adName}`}>
        <span aria-hidden="true">▧</span>
        <small>Preview not returned</small>
      </div>
    );
  }
  return (
    <img
      className="live-meta-creative-image"
      src={url}
      alt={`Meta creative preview for ${adName}`}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

// Presentation-only semantic accent for metric values (spend = violet,
// impressions = cyan, clicks = electric blue, everything else = white). The
// accent communicates metric identity, never performance quality.
function metricAccent(label) {
  const text = String(label || "").toLowerCase();
  if (text.includes("spend")) return "spend";
  if (text.includes("impression")) return "impressions";
  if (text.includes("ctr")) return "ctr";
  if (text.includes("click")) return "clicks";
  return "neutral";
}

function Metric({ label, value }) {
  return (
    <div className="live-meta-detail-metric" data-metric-accent={metricAccent(label)}>
      <span>{label}</span>
      <strong className="live-meta-data-value">{value}</strong>
    </div>
  );
}

// Meta delivery that belongs to neither Facebook nor Instagram — Audience
// Network, Messenger, and rows Meta labels "unknown". It is never added into
// the selected platform's totals, but it is never hidden either: silently
// dropping it would make the platform figures look like the whole Meta
// picture when they are not.
export function SeparatelyGovernedDelivery({ entries, platformLabel }) {
  const rows = (entries || []).filter((entry) => entry && entry.hasDelivery);
  if (!rows.length) return null;

  return (
    <div className="live-meta-separate-delivery">
      <p className="live-meta-attribution-note">
        Meta also delivered this campaign on placements outside {platformLabel}. These are
        reported separately and are <strong>not</strong> included in the {platformLabel} totals
        above.
      </p>
      <div className="live-meta-summary-grid">
        {rows.map((entry) => (
          <Metric
            key={entry.publisherPlatform}
            label={`${entry.label} spend`}
            value={formatMetaCurrency(entry.spend)}
          />
        ))}
        {rows.map((entry) => (
          <Metric
            key={`${entry.publisherPlatform}-impressions`}
            label={`${entry.label} impressions`}
            value={formatMetaInteger(entry.impressions)}
          />
        ))}
      </div>
    </div>
  );
}

export function filterAndSortMetaAds(
  ads,
  { search = "", status = "all", performance = "all", creative = "all", sort = "spend_desc" } = {}
) {
  const needle = String(search || "").trim().toLowerCase();
  const filtered = (Array.isArray(ads) ? ads : []).filter((ad) => {
    const searchText = [ad.adName, ad.campaignName, ad.adSetName].join(" ").toLowerCase();
    if (needle && !searchText.includes(needle)) return false;
    if (status !== "all" && String(ad.effectiveStatus || "").toLowerCase() !== status) return false;
    if (performance === "delivery" && !ad.performance?.hasDelivery) return false;
    if (performance === "no_delivery" && ad.performance?.hasDelivery) return false;
    if (creative !== "all" && String(ad.creative?.format || "unknown").toLowerCase() !== creative) {
      return false;
    }
    return true;
  });

  const metric = sort.replace(/_desc$/, "");
  return [...filtered].sort((a, b) => {
    if (sort === "ad_asc") return a.adName.localeCompare(b.adName);
    if (sort === "campaign_asc") return a.campaignName.localeCompare(b.campaignName);
    return (numeric(b.performance?.[metric]) ?? -1) - (numeric(a.performance?.[metric]) ?? -1);
  });
}

export default function LiveMetaAdsLibrary({
  advertiserId,
  platformKey,
  platformLabel,
  reportingSnapshot = null,
  showOperatorDiagnostics = false,
}) {
  const [datePreset, setDatePreset] = useState("maximum");
  const [campaignKey, setCampaignKey] = useState("all");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [performanceFilter, setPerformanceFilter] = useState("all");
  const [creativeFilter, setCreativeFilter] = useState("all");
  const [sort, setSort] = useState("spend_desc");
  const [expandedRow, setExpandedRow] = useState("");

  useEffect(() => {
    if (!advertiserId || !["facebook", "instagram"].includes(platformKey)) {
      setPayload(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    const hasExistingPayload = Boolean(payload);
    setLoading(!hasExistingPayload);
    setRefreshing(hasExistingPayload);
    setError(null);

    getLiveMetaAdsLibrary({
      advertiserId,
      platform: platformKey,
      datePreset,
      campaignKey,
      since: datePreset === "custom" ? since : null,
      until: datePreset === "custom" ? until : null,
      refresh: forceRefresh,
      signal: controller.signal,
    })
      .then((nextPayload) => {
        if (cancelled) return;
        setPayload(nextPayload);
        setStale(false);
        setError(null);
      })
      .catch((nextError) => {
        if (cancelled || controller.signal.aborted) return;
        setError(nextError);
        setStale(hasExistingPayload);
        if (!hasExistingPayload) setPayload(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
        setForceRefresh(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // payload is intentionally excluded: it is retained only as clearly stale
    // evidence when a subsequent fresh read fails.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertiserId, campaignKey, datePreset, platformKey, requestVersion, since, until]);

  useEffect(() => {
    setSearch("");
    setStatusFilter("all");
    setPerformanceFilter("all");
    setCreativeFilter("all");
    setExpandedRow("");
    setPayload(null);
    setCampaignKey("all");
    setDatePreset("maximum");
    setSince("");
    setUntil("");
  }, [advertiserId, platformKey]);

  const ads = useMemo(
    () => (Array.isArray(payload?.ads) ? payload.ads : []),
    [payload]
  );
  const statuses = useMemo(
    () =>
      Array.from(
        new Set(ads.map((ad) => String(ad.effectiveStatus || "").toLowerCase()).filter(Boolean))
      ).sort(),
    [ads]
  );
  const formats = useMemo(
    () =>
      Array.from(
        new Set(ads.map((ad) => String(ad.creative?.format || "unknown").toLowerCase()))
      ).sort(),
    [ads]
  );
  const visibleAds = useMemo(
    () =>
      filterAndSortMetaAds(ads, {
        search,
        status: statusFilter,
        performance: performanceFilter,
        creative: creativeFilter,
        sort,
      }),
    [ads, creativeFilter, performanceFilter, search, sort, statusFilter]
  );

  const handleRefresh = () => {
    setForceRefresh(true);
    setRequestVersion((version) => version + 1);
  };
  const presentation = errorPresentation(error);
  const campaignOptions = Array.isArray(payload?.scope?.campaignOptions)
    ? payload.scope.campaignOptions
    : [];
  const liveSummary = payload?.summary || null;
  const deliveryCounts = summaryDeliveryCounts(payload);
  const selectedCampaignLabel =
    campaignKey === "all"
      ? "All Meta campaigns"
      : campaignOptions.find((campaign) => campaign.key === campaignKey)?.name ||
        payload?.scope?.campaignName ||
        "Selected Meta campaign";
  const selectedTimeframeLabel =
    DATE_PRESETS.find((option) => option.value === datePreset)?.label ||
    "Selected reporting timeframe";

  return (
    <section className="live-meta-library live-meta-dark" data-testid="live-meta-ads-library">
      <div className="live-meta-reporting-scope" aria-label="Meta reporting scope">
        <div className="live-meta-scope-intro">
          <div className="live-meta-eyebrow">Reporting Scope</div>
          <h2 className="live-meta-major-heading">{platformLabel} campaign and timeframe</h2>
          <p>These controls govern both Performance Intelligence and the Live Meta Ads Library below.</p>
        </div>
        <label className="live-meta-scope-control">
          <span>Campaign</span>
          <select
            className="live-meta-scope-select"
            aria-label="Campaign"
            aria-busy={loading || refreshing}
            title={selectedCampaignLabel}
            value={campaignKey}
            onChange={(event) => {
              const next = event.target.value;
              setPayload(null);
              setCampaignKey(next);
              if (next === "all" && datePreset === "campaign_flight") setDatePreset("maximum");
            }}
            disabled={loading || refreshing}
          >
            <option value="all">All Meta campaigns</option>
            {campaignOptions.map((campaign) => (
              <option key={campaign.key} value={campaign.key}>{campaign.name}</option>
            ))}
          </select>
        </label>
        <label className="live-meta-scope-control">
          <span>Timeframe</span>
          <select
            className="live-meta-scope-select"
            aria-label="Timeframe"
            aria-busy={loading || refreshing}
            title={selectedTimeframeLabel}
            value={datePreset}
            onChange={(event) => {
              const next = event.target.value;
              setPayload(null);
              if (next === "custom" && (!since || !until)) {
                // Seed the custom range with the 30 Eastern calendar days
                // ending today (it was the UTC date).
                const end = reportingToday();
                setSince(addCalendarDays(end, -29));
                setUntil(end);
              }
              setDatePreset(next);
            }}
            disabled={loading || refreshing}
          >
            {DATE_PRESETS.filter((option) => option.value !== "campaign_flight" || campaignKey !== "all").map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        {datePreset === "custom" ? (
          <div className="live-meta-custom-dates">
            <label><span>Start</span><input className="live-meta-scope-date live-meta-data-value" type="date" value={since} onChange={(event) => { setPayload(null); setSince(event.target.value); }} /></label>
            <label><span>End</span><input className="live-meta-scope-date live-meta-data-value" type="date" value={until} onChange={(event) => { setPayload(null); setUntil(event.target.value); }} /></label>
          </div>
        ) : null}
      </div>

      {payload ? (
        <section
          className="live-meta-performance-card"
          aria-label="Performance Intelligence"
        >
          <div className="live-meta-performance-heading">
            <div><div className="live-meta-eyebrow">Live ads_read reporting</div><h2 className="live-meta-major-heading">Performance Intelligence</h2></div>
            <span className="live-meta-badge live-meta-badge-primary">FRESH META DATA</span>
          </div>
          <p><strong>{payload.scope?.campaignName || "All Meta campaigns"}</strong> · <span className="live-meta-data-value">{reportingDates(payload.reportingRange, payload.reportingRange)}</span></p>
          <div className="live-meta-summary-grid">
            <Metric label="Spend" value={formatMetaCurrency(liveSummary?.spend)} />
            <Metric label="Impressions" value={formatMetaInteger(liveSummary?.impressions)} />
            <Metric label="Clicks" value={formatMetaInteger(liveSummary?.clicks)} />
            <Metric label="CTR" value={formatMetaPercent(liveSummary?.ctr)} />
            <Metric label="CPC" value={formatMetaCurrency(liveSummary?.cpc)} />
            <Metric label="CPM" value={formatMetaCurrency(liveSummary?.cpm)} />
          </div>
          <p className="live-meta-attribution-note">{payload.platformAttribution?.note} Retrieved <span className="live-meta-data-value">{formatTimestamp(payload.retrievedAt)}</span>.</p>
          <SeparatelyGovernedDelivery
            entries={liveSummary?.separatelyGovernedDelivery}
            platformLabel={payload.platformAttribution?.platformLabel || platformLabel}
          />
        </section>
      ) : null}

      {reportingSnapshot ? (
        <details className="live-meta-snapshot-card">
          <summary>Cphere Campaign Reporting Snapshot</summary>
          <p>{reportingSnapshot.summary}</p>
          <dl className="live-meta-detail-facts">
            <div><dt>Campaign</dt><dd>{reportingSnapshot.campaignName || "Campaign not identified"}</dd></div>
            <div><dt>Reporting dates</dt><dd className="live-meta-data-value">{reportingSnapshot.dateLabel || "Not available"}</dd></div>
            <div><dt>Source</dt><dd>{reportingSnapshot.sourceLabel || "Recorded/imported Cphere reporting snapshot"}</dd></div>
          </dl>
          <div className="live-meta-summary-grid">
            {(reportingSnapshot.metrics || []).map((metric) => <Metric key={metric.label} label={metric.label} value={metric.value} />)}
          </div>
          <p className="live-meta-no-substitution">This historical snapshot is shown for provenance only. It is not merged into or substituted for the live Meta totals above.</p>
        </details>
      ) : null}

      <header className="live-meta-library-header">
        <div>
          <div className="live-meta-eyebrow">Live Meta API Reporting</div>
          <div className="live-meta-title-row">
            <h2 className="live-meta-major-heading">Live Meta Ads Library</h2>
            <span className="live-meta-badge live-meta-badge-primary">LIVE META DATA · ADS_READ</span>
            <span className="live-meta-badge">{platformLabel}</span>
            <span className="live-meta-badge live-meta-badge-readonly">READ-ONLY</span>
          </div>
          <p>
            Campaign, ad-set, creative, and delivery information retrieved from the connected
            advertiser’s Meta account through read-only authorization.
          </p>
        </div>
        <button
          type="button"
          className="live-meta-refresh"
          onClick={handleRefresh}
          disabled={loading || refreshing || !advertiserId}
        >
          {refreshing ? "Refreshing…" : "Refresh from Meta"}
        </button>
      </header>

      {payload ? (
        <div className="live-meta-provenance-bar">
          <span><b>Account</b> <span className="live-meta-data-value">{payload.linkedAccount || "Masked"}</span></span>
          <span><b>Range</b> <span className="live-meta-data-value">{reportingDates(payload.reportingRange, payload.reportingRange)}</span></span>
          <span><b>Retrieved</b> <span className="live-meta-data-value">{formatTimestamp(payload.retrievedAt)}</span></span>
          <span><b>Source</b> Retrieved from Meta Marketing API</span>
          <span className={stale ? "live-meta-state-stale" : "live-meta-state-fresh"}>
            {stale ? "STALE · REFRESH FAILED" : payload.cache?.state === "fresh" ? "FRESH" : "CACHED"}
          </span>
        </div>
      ) : null}

      {loading ? (
        <div className="live-meta-state-card" role="status">
          <span className="live-meta-loader" aria-hidden="true" />
          <div>
            <strong>Retrieving live ads and performance from Meta…</strong>
            <p>Using the advertiser’s connected Meta USER OAuth and read-only `ads_read` access.</p>
          </div>
        </div>
      ) : null}

      {error && !payload ? (
        <div className="live-meta-state-card live-meta-state-card-error" role="alert">
          <div>
            <strong>{presentation.title}</strong>
            <p>{presentation.message}</p>
            {presentation.showIntegrations ? <a href="/integrations">Open Integrations</a> : null}
            {!presentation.showIntegrations ? (
              <button type="button" onClick={handleRefresh}>Try again</button>
            ) : null}
          </div>
        </div>
      ) : null}

      {error && payload ? (
        <div className="live-meta-inline-warning" role="alert">
          <strong>{presentation.title}.</strong> Previously retrieved data remains visible and is
          labeled stale. {presentation.message}
        </div>
      ) : null}

      {payload?.partial?.isPartial ? (
        <div className="live-meta-inline-warning" role="status">
          <strong>Partial Meta response.</strong> {payload.partial.message}
        </div>
      ) : null}

      {payload?.reportingRange?.rangeAdjusted ? (
        <div className="live-meta-inline-warning" role="status">
          <strong>Effective reporting range adjusted.</strong> {payload.reportingRange.adjustmentReason}
        </div>
      ) : null}

      {payload ? (
        <>
          <div className="live-meta-summary-grid" aria-label="Live Meta summary">
            <Metric label="Ads readable" value={formatMetaInteger(payload.summary?.adsReadable)} />
            <Metric label="Ads with delivery" value={formatMetaInteger(payload.summary?.adsWithDelivery)} />
            <Metric label="No delivery confirmed" value={formatMetaInteger(deliveryCounts.confirmedZero)} />
            <Metric label="Ad-level reporting unavailable" value={formatMetaInteger(deliveryCounts.unavailable)} />
            <Metric label={`${platformLabel} spend`} value={formatMetaCurrency(payload.summary?.spend)} />
            <Metric label="Impressions" value={formatMetaInteger(payload.summary?.impressions)} />
            <Metric label="Clicks" value={formatMetaInteger(payload.summary?.clicks)} />
            <Metric label="Aggregate CTR" value={formatMetaPercent(payload.summary?.ctr)} />
          </div>

          <p className="live-meta-attribution-note">{payload.platformAttribution?.note}</p>

          {ads.length ? (
            <>
              <div className="live-meta-toolbar">
                <label className="live-meta-search">
                  <span className="live-meta-armor-label">Search live ads</span>
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Ad, campaign, or ad set"
                  />
                </label>
                <label>
                  <span className="live-meta-armor-label">Status</span>
                  <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    <option value="all">All statuses</option>
                    {statuses.map((status) => <option key={status} value={status}>{humanize(status)}</option>)}
                  </select>
                </label>
                <label>
                  <span className="live-meta-armor-label">Performance</span>
                  <select value={performanceFilter} onChange={(event) => setPerformanceFilter(event.target.value)}>
                    <option value="all">All ads</option>
                    <option value="delivery">With delivery</option>
                    <option value="no_delivery">Without delivery</option>
                  </select>
                </label>
                <label>
                  <span className="live-meta-armor-label">Creative format</span>
                  <select value={creativeFilter} onChange={(event) => setCreativeFilter(event.target.value)}>
                    <option value="all">All formats</option>
                    {formats.map((format) => <option key={format} value={format}>{humanize(format)}</option>)}
                  </select>
                </label>
                <label>
                  <span className="live-meta-armor-label">Sort</span>
                  <select value={sort} onChange={(event) => setSort(event.target.value)}>
                    {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              </div>

              <div className="live-meta-table-wrap">
                <table className="live-meta-table">
                  <colgroup>
                    <col className="live-meta-col-creative" />
                    <col className="live-meta-col-ad" />
                    <col className="live-meta-col-campaign" />
                    <col className="live-meta-col-adset" />
                    <col className="live-meta-col-status" />
                    <col className="live-meta-col-metric" />
                    <col className="live-meta-col-metric" />
                    <col className="live-meta-col-metric" />
                    <col className="live-meta-col-metric" />
                    <col className="live-meta-col-dates" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="live-meta-armor-label">Creative</th>
                      <th className="live-meta-armor-label">Ad</th>
                      <th className="live-meta-armor-label">Campaign</th>
                      <th className="live-meta-armor-label">Ad Set</th>
                      <th className="live-meta-armor-label">Status</th>
                      <th className="live-meta-armor-label">Spend</th>
                      <th className="live-meta-armor-label">Impressions</th>
                      <th className="live-meta-armor-label">Clicks</th>
                      <th className="live-meta-armor-label">CTR</th>
                      <th className="live-meta-armor-label">Reporting Dates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleAds.map((ad) => {
                      const expanded = expandedRow === ad.rowId;
                      const reportingView = adReportingPresentation(ad, platformLabel);
                      return (
                        <React.Fragment key={ad.rowId}>
                          <tr
                            className={expanded ? "live-meta-row-expanded" : ""}
                            onClick={() => setExpandedRow(expanded ? "" : ad.rowId)}
                          >
                            <td><CreativePreview creative={ad.creative} adName={ad.adName} /></td>
                            <td>
                              <button
                                type="button"
                                className="live-meta-row-toggle"
                                aria-expanded={expanded}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setExpandedRow(expanded ? "" : ad.rowId);
                                }}
                              >
                                <strong title={ad.adName}>{ad.adName}</strong>
                                <span>{expanded ? "Hide details" : "View details"}</span>
                              </button>
                            </td>
                            <td title={ad.campaignName}>{ad.campaignName}</td>
                            <td title={ad.adSetName}>{ad.adSetName}</td>
                            <td className="live-meta-status-cell"><span className={`live-meta-status live-meta-status-${String(ad.effectiveStatus || "").toLowerCase()}`}>{humanize(ad.effectiveStatus)}</span></td>
                            {reportingView.showMetrics ? (
                              <>
                                <td className="live-meta-data-value">{formatMetaCurrency(ad.performance?.spend)}</td>
                                <td className="live-meta-data-value">{formatMetaInteger(ad.performance?.impressions)}</td>
                                <td className="live-meta-data-value">{formatMetaInteger(ad.performance?.clicks)}</td>
                                <td className="live-meta-data-value">{formatMetaPercent(ad.performance?.ctr)}</td>
                              </>
                            ) : (
                              <td colSpan="4">
                                <span
                                  className="live-meta-row-reporting-state"
                                  data-reporting-state={reportingView.state}
                                >
                                  {reportingView.headline}
                                </span>
                              </td>
                            )}
                            <td className="live-meta-data-value">{reportingDates(ad.performance, payload.reportingRange)}</td>
                          </tr>
                          {expanded ? (
                            <tr className="live-meta-detail-row">
                              <td colSpan="10">
                                <div className="live-meta-detail-layout">
                                  <div className="live-meta-detail-preview">
                                    <CreativePreview creative={ad.creative} adName={ad.adName} />
                                    <span>{humanize(ad.creative?.format)} creative</span>
                                  </div>
                                  <div className="live-meta-detail-content">
                                    <div className="live-meta-detail-heading">
                                      <div>
                                        <h3 title={ad.adName}>{ad.adName}</h3>
                                        <p title={`${ad.campaignName} · ${ad.adSetName}`}>{ad.campaignName} · {ad.adSetName}</p>
                                      </div>
                                      <span className="live-meta-status">{humanize(ad.effectiveStatus)}</span>
                                    </div>
                                    {(() => {
                                      const reportingView = adReportingPresentation(ad, platformLabel);
                                      return (
                                        <>
                                          {reportingView.headline || reportingView.explanation ? (
                                            <div
                                              className="live-meta-reporting-panel"
                                              data-reporting-state={reportingView.state}
                                              data-testid="live-meta-reporting-panel"
                                              role="status"
                                            >
                                              {reportingView.headline ? <strong>{reportingView.headline}</strong> : null}
                                              {reportingView.explanation ? <p>{reportingView.explanation}</p> : null}
                                              {reportingView.retryable ? (
                                                <button type="button" className="live-meta-reporting-retry" onClick={handleRefresh}>
                                                  Retry retrieval
                                                </button>
                                              ) : null}
                                              {reportingView.showIntegrations ? (
                                                <a href="/integrations">Open Integrations</a>
                                              ) : null}
                                            </div>
                                          ) : null}
                                          {reportingView.showMetrics ? (
                                            <div className="live-meta-detail-grid">
                                              <Metric label="Spend" value={formatMetaCurrency(ad.performance?.spend)} />
                                              <Metric label="Impressions" value={formatMetaInteger(ad.performance?.impressions)} />
                                              <Metric label="Reach" value={formatMetaInteger(ad.performance?.reach)} />
                                              <Metric label="Clicks" value={formatMetaInteger(ad.performance?.clicks)} />
                                              <Metric label="CTR" value={formatMetaPercent(ad.performance?.ctr)} />
                                              <Metric label="CPC" value={formatMetaCurrency(ad.performance?.cpc)} />
                                              <Metric label="CPM" value={formatMetaCurrency(ad.performance?.cpm)} />
                                              <Metric label="Frequency" value={formatMetaDecimal(ad.performance?.frequency)} />
                                            </div>
                                          ) : null}
                                          <dl className="live-meta-detail-facts">
                                            <div>
                                              <dt>Platform attribution</dt>
                                              <dd>
                                                {ad.performance?.attributedPublishers?.length
                                                  ? ad.performance.attributedPublishers.join(", ")
                                                  : reportingView.state === "no_delivery"
                                                  ? `${platformLabel} — no delivery recorded`
                                                  : reportingView.state === "outside_window"
                                                  ? `${platformLabel} — no delivery in selected dates`
                                                  : ["retrieval_failed", "permission_denied"].includes(reportingView.state)
                                                  ? `${platformLabel} — not retrieved`
                                                  : `${platformLabel} — no attribution returned`}
                                              </dd>
                                            </div>
                                            <div><dt>Ad status</dt><dd>{humanize(ad.statusContext?.ad?.configuredStatus || ad.configuredStatus)}{ad.statusContext?.ad?.effectiveStatus && ad.statusContext.ad.effectiveStatus !== ad.statusContext.ad.configuredStatus ? ` (effective: ${humanize(ad.statusContext.ad.effectiveStatus)})` : ""}</dd></div>
                                            {ad.statusContext?.adSet ? (
                                              <div><dt>Ad set status</dt><dd>{humanize(ad.statusContext.adSet.effectiveStatus)}</dd></div>
                                            ) : null}
                                            {ad.statusContext?.campaign ? (
                                              <div><dt>Campaign status</dt><dd>{humanize(ad.statusContext.campaign.effectiveStatus)}</dd></div>
                                            ) : null}
                                            <div><dt>Reporting dates</dt><dd className="live-meta-data-value">{reportingDates(ad.performance, payload.reportingRange)}</dd></div>
                                            <div><dt>Retrieved</dt><dd className="live-meta-data-value">{formatTimestamp(ad.retrievedAt)}</dd></div>
                                            <div><dt>Source</dt><dd>Retrieved from Meta Marketing API</dd></div>
                                          </dl>
                                          {showOperatorDiagnostics ? (
                                            <OperatorDiagnostics ad={ad} payload={payload} />
                                          ) : null}
                                        </>
                                      );
                                    })()}
                                    <p className="live-meta-no-substitution">
                                      {payload.provenance?.statement}
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!visibleAds.length ? (
                <div className="live-meta-filter-empty">No live Meta ads match the current search and filters.</div>
              ) : null}
            </>
          ) : (
            <div className="live-meta-state-card">
              <div>
                <strong>The linked account is accessible, but Meta returned no readable ads.</strong>
                <p>No stored or imported advertising records were substituted.</p>
              </div>
            </div>
          )}

          {ads.length > 0 && Number(payload.summary?.adsWithDelivery || 0) === 0 ? (
            <div className="live-meta-inline-note">
              Ads were retrieved successfully. Meta returned no delivery data for this platform and reporting period.
            </div>
          ) : null}

          <footer className="live-meta-readonly-statement">
            <strong>This library is read-only.</strong> Cphere retrieves advertising information
            through `ads_read` and cannot create, edit, activate, pause, publish, or delete Meta
            advertising from this view. The analytics content above may use a separate Cphere
            reporting snapshot and is not combined with these live Meta totals.
          </footer>
        </>
      ) : null}
    </section>
  );
}
